import { useCallback, useEffect, useMemo, useState } from 'react';

import { FieldNames, PaymentMethodInput } from '@cashier/cashier-model';

interface UseHostedFieldHandlersParams {
  schema: Map<FieldNames, PaymentMethodInput>;
  customInputChange?: (
    name: string,
    value: string,
    isValid: boolean,
    isFocused: boolean,
    isEmpty: boolean,
  ) => void;
}

interface UseHostedFieldHandlersResult {
  handleLeaderMessage: (payload: any) => void;
  handleIframeLoad: (FieldName: FieldNames) => void;
  isEveryIframeLoaded: boolean;
}

export const useHostedFieldHandlers = ({
  schema,
  customInputChange,
}: UseHostedFieldHandlersParams): UseHostedFieldHandlersResult => {
  const [onLoadIframeState, setOnLoadIframeState] = useState<
    Partial<Record<FieldNames, boolean>>
  >({});

  useEffect(() => {
    setOnLoadIframeState((prev) => {
      const newState: Partial<Record<FieldNames, boolean>> = {};
      for (const k of schema.keys()) {
        newState[k] = prev[k] ?? false;
      }
      return newState;
    });
  }, [schema]);

  const handleIframeLoad = useCallback((FieldName: FieldNames) => {
    setOnLoadIframeState((prev) => ({
      ...prev,
      [FieldName]: true,
    }));
  }, []);

  const isEveryIframeLoaded = useMemo(
    () => Object.values(onLoadIframeState).every(Boolean),
    [onLoadIframeState],
  );

  const handleLeaderMessage = useCallback(
    (payload: { fields: PaymentMethodInput[] }) => {
      const fieldsFromLeader = payload?.fields;

      fieldsFromLeader.forEach((fieldItem) => {
        if (!fieldItem) return;

        customInputChange(
          fieldItem.name,
          String(fieldItem.value),
          Boolean(fieldItem.isValid),
          Boolean(fieldItem.isFocused),
          fieldItem.isEmpty,
        );
      });
    },
    [customInputChange],
  );

  return {
    handleLeaderMessage,
    handleIframeLoad,
    isEveryIframeLoaded,
  };
};
